import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';

import { catchError, throwError } from 'rxjs';

import { AuthSessionService } from '@common/service/auth-session.service';
import { FeatureFlagService } from '@common/service/feature-flag.service';
import { HoldableSource, SessionExpiryService } from '@common/service/session-expiry.service';
import { UserService } from '@common/service/user.service';

/** §5a — statuses that may mean "cookie gone" and are therefore held for a decision. */
function holdSource(status: number): HoldableSource | null {
  return status === 401 ? 'http-401' : status === 403 ? 'http-403' : status === 404 ? 'http-404' : null;
}

/** Auth endpoints must never trigger their own re-auth (would loop). */
function isAuthEndpoint(url: string): boolean {
  return url.includes('/api/auth');
}

/**
 * Step-up trigger (AK-474 WP-A4 contract, 2026-06-25): the server signals it via
 * the response body `{ error: 'step_up_required', step_up_method: '...' }`. The
 * carrying HTTP status is not specified, so match on the body regardless of code.
 */
function isStepUpRequired(error: HttpErrorResponse): boolean {
  const body = error.error;
  return !!body && typeof body === 'object' && body.error === 'step_up_required';
}

/**
 * Session-expired & step-up handling for cookie-session mode (AK-476 WP-F5,
 * AIWB-1088).
 *
 * On a non-auth API call:
 *  - a `step_up_required` body (a sensitive endpoint demands elevation) still
 *    triggers a single re-auth round trip (UserService.login → navigate to
 *    /api/auth/login/start with target = current location) — UNCHANGED from
 *    AK-474 WP-A4, out of scope for AIWB-1088.
 *  - an eligible 401/403/404 — a plain 401 means the `aiwb_session` cookie
 *    expired/was revoked; a 403 or 404 (owner testing gap found post-AIWB-1088)
 *    means the backend hid private content from an unauthenticated caller
 *    behind an ordinary 403/404 rather than a 401 — is held via
 *    `SessionExpiryService.hold()` (AIWB-1088 §5a) instead of clearing the
 *    session, redirecting or rethrowing immediately. It is delivered
 *    unchanged when the confirm says authenticated, when the confirm hits an
 *    outage, or when a 403/404 falls in the soft throttle window; it stays
 *    held while the tab is expired and is replayed exactly once after a
 *    same-user restore (dropped on a different-user reload).
 *
 * Safety gates (all three branches):
 * - Only when the migration toggle is ON (dormant otherwise — today's behaviour).
 * - Only when a session was PREVIOUSLY authenticated. This distinguishes a
 *   mid-session expiry/step-up from an unauthenticated guest/public load (which
 *   must stay on the page; the loginGuard handles opt-in login).
 */
export const authErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const featureFlag = inject(FeatureFlagService);
  const authSession = inject(AuthSessionService);
  const userService = inject(UserService);
  const sessionExpiry = inject(SessionExpiryService);

  return next(req).pipe(
    catchError((error) => {
      const eligible =
        error instanceof HttpErrorResponse &&
        featureFlag.cookieSessionMigrationEnabled &&
        authSession.isAuthenticated &&
        !isAuthEndpoint(req.url);

      if (eligible && isStepUpRequired(error)) {
        // Step-up: unchanged (AK-474 WP-A4) — out of scope for AIWB-1088.
        authSession.clear();
        userService.login({
          pathname: window.location.pathname,
          search: window.location.search,
          hash: window.location.hash,
        });
        return throwError(() => error);
      }
      const source = eligible ? holdSource(error.status) : null;
      if (source) {
        // AIWB-1088 §5a: hold until SessionExpiryService decides. `() => next(req)` re-issues the
        // request DOWNSTREAM of this catchError — a replay can never be held again (no loop), and
        // the upstream requestTransformer (already applied to `req`) does not run twice.
        return sessionExpiry.hold(source, { method: req.method, url: req.url, status: error.status }, error, () =>
          next(req),
        );
      }
      return throwError(() => error);
    }),
  );
};
